import React, { useState, useEffect } from 'react';
import {
  Users,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  Phone,
  Mail,
  Building2,
  Shield,
  Voicemail,
  CheckCircle,
  XCircle,
  Download,
  Upload,
  RefreshCw,
  Loader2,
  Wifi,
  WifiOff,
  Key,
  Send
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { extensionsAPI, authAPI } from '../services/api';
import { useRealtime } from '../context/RealtimeContext';

const departments = [
  { id: 1, name: "Sales" },
  { id: 2, name: "Support" },
  { id: 3, name: "Engineering" },
  { id: 4, name: "HR" },
  { id: 5, name: "Finance" },
  { id: 6, name: "Marketing" },
  { id: 7, name: "Front Desk" },
  { id: 8, name: "Facilities" },
  { id: 9, name: "Management" }
];

const Extensions = () => {
  const [extensions, setExtensions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedExtension, setSelectedExtension] = useState(null);
  const [newExtension, setNewExtension] = useState({
    id: '',
    name: '',
    email: '',
    department: '',
    phone: 'Softphone',
    voicemail: true,
    role: 'User'
  });

  // Real-time status updates
  const { isConnected, extensionStatus, statusChanges } = useRealtime();

  // Merge real-time status with extension data
  const extensionsWithRealtimeStatus = extensions.map(ext => {
    const realtimeStatus = extensionStatus[ext.id];
    if (realtimeStatus) {
      return {
        ...ext,
        status: realtimeStatus.status,
        phoneStatus: realtimeStatus.phone_status
      };
    }
    return ext;
  });

  const filteredExtensions = extensionsWithRealtimeStatus.filter((ext) => {
    const matchesSearch =
      ext.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ext.id.includes(searchQuery) ||
      (ext.email || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ext.status === statusFilter;
    const matchesDepartment = departmentFilter === 'all' || ext.department === departmentFilter;
    return matchesSearch && matchesStatus && matchesDepartment;
  });

  useEffect(() => {
    fetchExtensions();
  }, []);

  const fetchExtensions = async () => {
    try {
      setLoading(true);
      const response = await extensionsAPI.list();
      // Transform backend data to frontend format
      const transformed = response.data.map(ext => ({
        id: ext.id,
        name: ext.name,
        email: ext.email || '',
        department: ext.department,
        status: ext.status,
        type: ext.type,
        phone: ext.phone,
        phoneStatus: ext.phone_status,
        voicemail: ext.voicemail,
        role: ext.role
      }));
      setExtensions(transformed);
    } catch (error) {
      console.error('Error fetching extensions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'busy': return 'bg-red-500';
      case 'away': return 'bg-yellow-500';
      case 'dnd': return 'bg-orange-500';
      case 'offline': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      online: 'bg-green-500/20 text-green-400 border-green-500/30',
      busy: 'bg-red-500/20 text-red-400 border-red-500/30',
      away: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      dnd: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      offline: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
    };
    return <Badge className={colors[status] || colors.offline}>{status}</Badge>;
  };

  const getPhoneStatusBadge = (status) => {
    if (status?.toLowerCase() === 'registered') {
      return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Registered</Badge>;
    }
    return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Unregistered</Badge>;
  };

  const handleAddExtension = async () => {
    try {
      await extensionsAPI.create({
        id: newExtension.id,
        name: newExtension.name,
        email: newExtension.email,
        department: newExtension.department,
        phone_model: newExtension.phone,
        voicemail_enabled: newExtension.voicemail,
        role: newExtension.role
      });
      setIsAddModalOpen(false);
      setNewExtension({
        id: '',
        name: '',
        email: '',
        department: '',
        phone: 'Softphone',
        voicemail: true,
        role: 'User'
      });
      await fetchExtensions();
      
      // Check if credentials are returned (email failed)
      if (response.data.credentials) {
        const creds = response.data.credentials;
        toast.warning('User Created - Email Failed', {
          description: (
            <div className="mt-1">
              <p className="text-xs text-gray-400 mb-2">{creds.message}</p>
              <div className="bg-black/30 rounded p-2 space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">Extension:</span>
                  <code className="text-yellow-300 font-mono text-sm">{newExtension.id}</code>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">Password:</span>
                  <code className="text-yellow-300 font-mono text-sm select-all">{creds.password}</code>
                </div>
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(creds.password);
                    toast.success('Password copied!', { duration: 2000 });
                  }}
                  className="w-full mt-2 text-xs bg-blue-600 hover:bg-blue-700 px-2 py-1 rounded text-white"
                >
                  Copy Password
                </button>
              </div>
            </div>
          ),
          duration: 60000  // 60 seconds to give user time to copy
        });
      } else {
        toast.success('User created successfully', {
          description: newExtension.email ? 'Credentials sent to email' : 'User added without email'
        });
      }
    } catch (error) {
      console.error('Error creating extension:', error);
      toast.error('Failed to create user', {
        description: error.response?.data?.detail || 'Please try again'
      });
    }
  };

  const handleUpdateExtension = async () => {
    try {
      await extensionsAPI.update(selectedExtension.id, {
        name: selectedExtension.name,
        email: selectedExtension.email,
        department: selectedExtension.department,
        phone_model: selectedExtension.phone,
        voicemail_enabled: selectedExtension.voicemail,
        role: selectedExtension.role
      });
      setIsEditModalOpen(false);
      await fetchExtensions();
      toast.success('User updated successfully');
    } catch (error) {
      console.error('Error updating extension:', error);
      toast.error('Failed to update user', {
        description: error.response?.data?.detail || 'Please try again'
      });
    }
  };

  const handleDeleteExtension = async (id, name) => {
    if (!confirm(`Are you sure you want to delete ${name}?`)) return;
    
    try {
      await extensionsAPI.delete(id);
      await fetchExtensions();
      toast.success('User deleted successfully');
    } catch (error) {
      console.error('Error deleting extension:', error);
      toast.error('Failed to delete user', {
        description: error.response?.data?.detail || 'Please try again'
      });
    }
  };

  const handleResetPassword = async (ext) => {
    if (!ext.email) {
      toast.error('Cannot reset password', {
        description: 'User has no email address'
      });
      return;
    }
    
    try {
      const response = await authAPI.resetPassword(ext.id);
      if (response.data.new_password) {
        const newPassword = response.data.new_password;
        // Email failed, show password directly with copy functionality
        toast.warning('Password Reset Complete', {
          description: (
            <div className="mt-1">
              <p className="text-xs text-gray-400 mb-1">Email could not be sent. New password:</p>
              <div className="flex items-center gap-2 bg-black/30 rounded px-2 py-1">
                <code className="text-yellow-300 font-mono text-sm select-all">{newPassword}</code>
                <button 
                  onClick={() => {
                    navigator.clipboard.writeText(newPassword);
                    toast.success('Password copied to clipboard!', { duration: 2000 });
                  }}
                  className="text-xs bg-blue-600 hover:bg-blue-700 px-2 py-0.5 rounded text-white"
                >
                  Copy
                </button>
              </div>
            </div>
          ),
          duration: 30000  // 30 seconds to give user time to copy
        });
      } else {
        toast.success('Password reset email sent', {
          description: `New password sent to ${ext.email}`
        });
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Failed to reset password', {
        description: error.response?.data?.detail || 'Please try again'
      });
    }
  };

  const uniqueDepartments = [...new Set(extensions.map((ext) => ext.department))];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Users</h1>
          <p className="text-gray-400 mt-1">Manage extensions and user accounts</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" className="border-[#2a2a3d] text-gray-400 hover:text-white hover:bg-[#1e1e2d]">
            <Upload className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button variant="outline" className="border-[#2a2a3d] text-gray-400 hover:text-white hover:bg-[#1e1e2d]">
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-[#12121a] border-[#1e1e2d]">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="w-5 h-5 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search by name, extension, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-[#1e1e2d] border-[#2a2a3d] text-white placeholder-gray-500"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px] bg-[#1e1e2d] border-[#2a2a3d] text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="busy">Busy</SelectItem>
                <SelectItem value="away">Away</SelectItem>
                <SelectItem value="dnd">Do Not Disturb</SelectItem>
                <SelectItem value="offline">Offline</SelectItem>
              </SelectContent>
            </Select>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-[180px] bg-[#1e1e2d] border-[#2a2a3d] text-white">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                <SelectItem value="all">All Departments</SelectItem>
                {uniqueDepartments.map((dept) => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" className="text-gray-400 hover:text-white">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Extensions Table */}
      <Card className="bg-[#12121a] border-[#1e1e2d]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1e1e2d]">
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Extension</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Name</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Email</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Department</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Phone</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Phone Status</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Role</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-400">Voicemail</th>
                  <th className="text-right p-4 text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredExtensions.map((ext) => (
                  <tr
                    key={ext.id}
                    className="border-b border-[#1e1e2d] hover:bg-[#1e1e2d]/50 transition-colors"
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${getStatusColor(ext.status)}`}></div>
                        <span className="text-white font-mono">{ext.id}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="text-white">{ext.name}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-gray-400">{ext.email || '-'}</span>
                    </td>
                    <td className="p-4">
                      <span className="text-gray-400">{ext.department}</span>
                    </td>
                    <td className="p-4">
                      {getStatusBadge(ext.status)}
                    </td>
                    <td className="p-4">
                      <span className="text-gray-400">{ext.phone}</span>
                    </td>
                    <td className="p-4">
                      {getPhoneStatusBadge(ext.phoneStatus)}
                    </td>
                    <td className="p-4">
                      <Badge variant="outline" className="border-[#2a2a3d] text-gray-300">
                        {ext.role}
                      </Badge>
                    </td>
                    <td className="p-4">
                      {ext.voicemail ? (
                        <CheckCircle className="w-5 h-5 text-green-400" />
                      ) : (
                        <XCircle className="w-5 h-5 text-gray-500" />
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-white">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#1e1e2d] border-[#2a2a3d]">
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedExtension(ext);
                              setIsEditModalOpen(true);
                            }}
                            className="text-gray-300 hover:text-white hover:bg-[#2a2a3d] cursor-pointer"
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleResetPassword(ext)}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/20 cursor-pointer"
                          >
                            <Key className="w-4 h-4 mr-2" />
                            Reset Password
                          </DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-[#2a2a3d]" />
                          <DropdownMenuItem
                            onClick={() => handleDeleteExtension(ext.id, ext.name)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20 cursor-pointer"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredExtensions.length === 0 && (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 mx-auto text-gray-600 mb-4" />
              <p className="text-gray-400">No extensions found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Extension Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="bg-[#12121a] border-[#1e1e2d] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Add New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Extension Number</Label>
                <Input
                  value={newExtension.id}
                  onChange={(e) => setNewExtension({ ...newExtension, id: e.target.value })}
                  placeholder="1013"
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Full Name</Label>
                <Input
                  value={newExtension.name}
                  onChange={(e) => setNewExtension({ ...newExtension, name: e.target.value })}
                  placeholder="John Doe"
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400">Email Address</Label>
              <Input
                value={newExtension.email}
                onChange={(e) => setNewExtension({ ...newExtension, email: e.target.value })}
                placeholder="john.doe@company.com"
                className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-400">Department</Label>
                <Select
                  value={newExtension.department}
                  onValueChange={(value) => setNewExtension({ ...newExtension, department: value })}
                >
                  <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Role</Label>
                <Select
                  value={newExtension.role}
                  onValueChange={(value) => setNewExtension({ ...newExtension, role: value })}
                >
                  <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                    <SelectItem value="User">User</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Receptionist">Receptionist</SelectItem>
                    <SelectItem value="System Owner">System Owner</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-gray-400">Phone Device</Label>
              <Select
                value={newExtension.phone}
                onValueChange={(value) => setNewExtension({ ...newExtension, phone: value })}
              >
                <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                  <SelectValue placeholder="Select phone" />
                </SelectTrigger>
                <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                  <SelectItem value="Softphone">Softphone</SelectItem>
                  <SelectItem value="Yealink T46U">Yealink T46U</SelectItem>
                  <SelectItem value="Yealink T54W">Yealink T54W</SelectItem>
                  <SelectItem value="Cisco SPA525G2">Cisco SPA525G2</SelectItem>
                  <SelectItem value="Grandstream GXP2170">Grandstream GXP2170</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-gray-400">Enable Voicemail</Label>
              <Switch
                checked={newExtension.voicemail}
                onCheckedChange={(checked) => setNewExtension({ ...newExtension, voicemail: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddModalOpen(false)} className="border-[#2a2a3d] text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleAddExtension} className="bg-blue-600 hover:bg-blue-700">
              Add User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Extension Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="bg-[#12121a] border-[#1e1e2d] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl">Edit User</DialogTitle>
          </DialogHeader>
          {selectedExtension && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Extension Number</Label>
                  <Input
                    value={selectedExtension.id}
                    disabled
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white opacity-50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Full Name</Label>
                  <Input
                    value={selectedExtension.name}
                    onChange={(e) => setSelectedExtension({ ...selectedExtension, name: e.target.value })}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">Email Address</Label>
                <Input
                  value={selectedExtension.email}
                  onChange={(e) => setSelectedExtension({ ...selectedExtension, email: e.target.value })}
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-400">Department</Label>
                  <Select
                    value={selectedExtension.department}
                    onValueChange={(value) => setSelectedExtension({ ...selectedExtension, department: value })}
                  >
                    <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                      {departments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Role</Label>
                  <Select
                    value={selectedExtension.role}
                    onValueChange={(value) => setSelectedExtension({ ...selectedExtension, role: value })}
                  >
                    <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                      <SelectItem value="User">User</SelectItem>
                      <SelectItem value="Manager">Manager</SelectItem>
                      <SelectItem value="Receptionist">Receptionist</SelectItem>
                      <SelectItem value="System Owner">System Owner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-gray-400">Enable Voicemail</Label>
                <Switch
                  checked={selectedExtension.voicemail}
                  onCheckedChange={(checked) => setSelectedExtension({ ...selectedExtension, voicemail: checked })}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditModalOpen(false)} className="border-[#2a2a3d] text-gray-400">
              Cancel
            </Button>
            <Button onClick={handleUpdateExtension} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Extensions;
